import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import NavBar from "@/components/NavBar";
import Logo from "@/components/Logo";
import { Link } from "react-router-dom";

const faqs = [
  {
    question: "What is FundOrFling and how does it work?",
    answer:
      "FundOrFling is a platform for validating product ideas with real users. Entrepreneurs can create campaigns to test their ideas, gather feedback, and make data-driven decisions before investing in development.",
  },
  {
    question: "How do I submit a new idea or campaign?",
    answer:
      "To submit a new campaign, log in to your account and click the “Launch Your Campaign” button. Fill in the required details and submit your idea for others to view and interact with.",
  },
  {
    question: "Can I vote or comment on ideas without an account?",
    answer:
      "You need to create an account or log in to vote or comment on campaigns. This helps us ensure a fair and constructive community.",
  },
  {
    question: "How are top ideas selected?",
    answer:
      "Top campaigns are determined by the number of votes and engagement (comments, responses) they receive from the community. The most popular and active campaigns are featured more prominently.",
  },
  {
    question: "Is my feedback anonymous?",
    answer:
      "You can choose to provide feedback anonymously on certain campaigns. However, some features may require you to be logged in for moderation and engagement tracking.",
  },
  {
    question: "How do I report inappropriate content?",
    answer:
      "If you come across content that violates our guidelines, use the “Report” button on the relevant campaign or comment. Our moderation team will review and take appropriate action.",
  },
];

const HelpSupport: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <NavBar />
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-4xl">
        {/* Back Button */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <span className="text-lg">💬</span>
            Support Center
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-primary-600 to-accent-600 bg-clip-text text-transparent">
            Help & Support
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Find answers to common questions about using FundOrFling. Our
            comprehensive FAQ covers everything you need to know.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 text-center">
            <div className="text-3xl font-bold text-primary-600 mb-2">6</div>
            <div className="text-sm text-slate-600">Common Questions</div>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 text-center">
            <div className="text-3xl font-bold text-accent-600 mb-2">24/7</div>
            <div className="text-sm text-slate-600">Support Available</div>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 text-center">
            <div className="text-3xl font-bold text-emerald-600 mb-2">98%</div>
            <div className="text-sm text-slate-600">Satisfaction Rate</div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-white/60 p-8 shadow-xl">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Click on any question below to expand and see the answer.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem
                value={`faq-${idx}`}
                key={idx}
                className="border border-slate-200 rounded-lg px-6 py-4 bg-white/50"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-primary-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-700 leading-relaxed pt-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Contact Section */}
        <div className="mt-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Still Need Help?</h3>
          <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            Can't find what you're looking for? Our support team is here to help
            you get the most out of FundOrFling.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              className="bg-white text-primary-600 hover:bg-slate-100"
            >
              Contact Support
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white/10"
            >
              View Documentation
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer>
        <div className="border-t border-slate-200 py-8 text-center text-slate-600">
          <p>
            &copy; {new Date().getFullYear()} FundOrFling. All rights reserved.
            Built for professional product teams.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HelpSupport;
